<div class="pa-page">
  <div class="pa-inner">
    <p class="pa-eyebrow">EXECUTION PARTNER APPLICATION</p>
    <h1 class="pa-title">Tell us about your work.</h1>
    <p class="pa-sub">
      Every field below exists to show real evidence, not just claims —
      links, shipped projects, the work you're proud of. Takes about 10
      minutes.
    </p>

    <div class="pa-login-banner" *ngIf="userChecked && !user">
      <mat-icon>lock</mat-icon>
      <span>You'll need to log in before submitting — you can fill out the form first.</span>
    </div>

    <div class="pa-checking" *ngIf="checkingExisting">
      <p>Checking your application status…</p>
    </div>

    <form class="pa-form" *ngIf="!checkingExisting" [formGroup]="form" (ngSubmit)="submit()">
      <!-- ── About you ────────────────────────────────────────────── -->
      <section class="pa-section">
        <h2 class="pa-section-title">About you</h2>

        <div class="pa-row">
          <div class="pa-field">
            <label>Full name</label>
            <input type="text" formControlName="fullName" placeholder="Your full name" />
            <p class="pa-error" *ngIf="fieldInvalid(form.get('fullName'))">Required.</p>
          </div>
          <div class="pa-field">
            <label>Email</label>
            <input type="email" formControlName="email" placeholder="you@example.com" />
            <p class="pa-error" *ngIf="fieldInvalid(form.get('email'))">A valid email is required.</p>
          </div>
        </div>

        <div class="pa-row">
          <div class="pa-field">
            <label>Phone (optional)</label>
            <input type="tel" formControlName="phone" placeholder="+91…" />
          </div>
          <div class="pa-field">
            <label>Location (optional)</label>
            <input type="text" formControlName="location" placeholder="City, Country" />
          </div>
        </div>

        <div class="pa-field">
          <label>One-line headline</label>
          <input
            type="text"
            formControlName="headline"
            placeholder="e.g. Frontend developer who ships pixel-perfect Angular UIs"
          />
          <p class="pa-error" *ngIf="fieldInvalid(form.get('headline'))">Required.</p>
        </div>

        <div class="pa-field">
          <label>Tell us about yourself and your best work</label>
          <textarea
            rows="4"
            formControlName="bio"
            placeholder="What you work on, what you're best at, what you're proud of…"
          ></textarea>
          <p class="pa-error" *ngIf="fieldInvalid(form.get('bio'))">
            Required — at least a few sentences (40+ characters).
          </p>
        </div>

        <div class="pa-row">
          <div class="pa-field">
            <label>Years of experience</label>
            <input type="number" min="0" max="60" formControlName="yearsExperience" placeholder="e.g. 3" />
            <p class="pa-error" *ngIf="fieldInvalid(form.get('yearsExperience'))">Required.</p>
          </div>
          <div class="pa-field">
            <label>Availability</label>
            <div class="pa-select-wrap">
              <select formControlName="availability">
                <option value="" disabled selected>Choose one</option>
                <option *ngFor="let opt of availabilityOptions" [value]="opt.value">{{ opt.label }}</option>
              </select>
              <mat-icon class="pa-select-chevron">expand_more</mat-icon>
            </div>
            <p class="pa-error" *ngIf="fieldInvalid(form.get('availability'))">Required.</p>
          </div>
        </div>
      </section>

      <!-- ── Role ─────────────────────────────────────────────────── -->
      <section class="pa-section">
        <h2 class="pa-section-title">Which role are you applying for?</h2>
        <div class="pa-role-grid">
          <label
            class="pa-role-option"
            *ngFor="let r of roles"
            [class.pa-role-option-selected]="form.get('role')?.value === r.role"
          >
            <input type="radio" formControlName="role" [value]="r.role" />
            <mat-icon>{{ r.icon }}</mat-icon>
            <span>{{ r.label }}</span>
          </label>
        </div>
        <p class="pa-error" *ngIf="fieldInvalid(form.get('role'))">Please choose a role.</p>
      </section>

      <!-- ── Role-specific fields ─────────────────────────────────── -->
      <section class="pa-section" *ngIf="selectedRoleFields.length" formGroupName="roleFields">
        <h2 class="pa-section-title">A bit more about your {{ form.get('role')?.value }} experience</h2>

        <div class="pa-field" *ngFor="let field of selectedRoleFields">
          <label>{{ field.label }}</label>
          <p class="pa-help" *ngIf="field.helpText">{{ field.helpText }}</p>

          <ng-container [ngSwitch]="field.type">
            <input
              *ngSwitchCase="'text'"
              type="text"
              [formControlName]="field.id"
              [placeholder]="field.placeholder ?? ''"
            />
            <textarea
              *ngSwitchCase="'textarea'"
              rows="3"
              [formControlName]="field.id"
              [placeholder]="field.placeholder ?? ''"
            ></textarea>
            <input
              *ngSwitchCase="'tel'"
              type="tel"
              [formControlName]="field.id"
              [placeholder]="field.placeholder ?? ''"
            />
            <input
              *ngSwitchCase="'url'"
              type="url"
              [formControlName]="field.id"
              [placeholder]="field.placeholder ?? ''"
            />
            <input
              *ngSwitchCase="'number'"
              type="number"
              [formControlName]="field.id"
              [placeholder]="field.placeholder ?? ''"
            />

            <div class="pa-select-wrap" *ngSwitchCase="'select'">
              <select [formControlName]="field.id">
                <option value="" disabled selected>Choose one</option>
                <option *ngFor="let opt of field.options" [value]="opt.value">{{ opt.label }}</option>
              </select>
              <mat-icon class="pa-select-chevron">expand_more</mat-icon>
            </div>

            <div class="pa-pill-group" *ngSwitchCase="'multiselect'">
              <button
                type="button"
                class="pa-pill"
                *ngFor="let opt of field.options"
                [class.pa-pill-active]="(roleFieldControl(field.id).value ?? []).includes(opt.value)"
                (click)="toggleMultiselectOption(field.id, opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>

            <div class="pa-chip-field" *ngSwitchCase="'chips'">
              <div class="pa-chip-list">
                <span class="pa-chip" *ngFor="let chip of roleFieldControl(field.id).value; let i = index">
                  {{ chip }}
                  <button type="button" (click)="removeChip(field.id, i)" aria-label="Remove">×</button>
                </span>
              </div>
              <input
                type="text"
                [placeholder]="field.placeholder ?? 'Type and press Enter'"
                [(ngModel)]="chipDraft[field.id]"
                [ngModelOptions]="{ standalone: true }"
                (keydown.enter)="$event.preventDefault(); addChip(field.id)"
                (blur)="addChip(field.id)"
              />
            </div>
          </ng-container>

          <p class="pa-error" *ngIf="fieldInvalid(roleFieldControl(field.id))">Required.</p>
        </div>
      </section>

      <!-- ── Notable projects ─────────────────────────────────────── -->
      <section class="pa-section" formArrayName="notableProjects">
        <h2 class="pa-section-title">Notable projects</h2>
        <p class="pa-help">
          Your strongest shipped work. Link it if you can — this is what
          reviewers look at first.
        </p>

        <div class="pa-project-card" *ngFor="let project of projectsArray.controls; let i = index" [formGroupName]="i">
          <div class="pa-project-header">
            <span class="pa-project-index">Project {{ i + 1 }}</span>
            <button
              type="button"
              class="pa-remove-btn"
              *ngIf="projectsArray.length > 1"
              (click)="removeProject(i)"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="pa-field">
            <label>Name</label>
            <input type="text" formControlName="name" placeholder="Project name" />
            <p class="pa-error" *ngIf="fieldInvalid(project.get('name'))">Required.</p>
          </div>
          <div class="pa-field">
            <label>Link (optional)</label>
            <input type="url" formControlName="url" placeholder="https://…" />
          </div>
          <div class="pa-field">
            <label>What you did</label>
            <textarea rows="2" formControlName="description" placeholder="Your role and what shipped"></textarea>
            <p class="pa-error" *ngIf="fieldInvalid(project.get('description'))">Required.</p>
          </div>
        </div>

        <button type="button" class="pa-add-btn" (click)="addProject()">
          <mat-icon>add</mat-icon> Add another project
        </button>
      </section>

      <!-- ── Links ────────────────────────────────────────────────── -->
      <section class="pa-section" formArrayName="links">
        <h2 class="pa-section-title">Links (optional)</h2>
        <p class="pa-help">LinkedIn, personal site, anything else worth seeing.</p>

        <div class="pa-link-row" *ngFor="let link of linksArray.controls; let i = index" [formGroupName]="i">
          <input type="text" formControlName="label" placeholder="LinkedIn" class="pa-link-label" />
          <input type="url" formControlName="url" placeholder="https://…" class="pa-link-url" />
          <button type="button" class="pa-remove-btn" (click)="removeLink(i)">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <button type="button" class="pa-add-btn" (click)="addLink()">
          <mat-icon>add</mat-icon> Add a link
        </button>
      </section>

      <!-- ── Resume ───────────────────────────────────────────────── -->
      <section class="pa-section">
        <h2 class="pa-section-title">Resume</h2>
        <label class="pa-resume-drop">
          <input type="file" accept=".pdf,.doc,.docx" (change)="onResumeSelected($event)" hidden />
          <mat-icon>upload_file</mat-icon>
          <span *ngIf="!resumeFileName">Upload your resume (PDF or Word, under 5MB)</span>
          <span *ngIf="resumeFileName" class="pa-resume-filename">{{ resumeFileName }}</span>
        </label>
        <p class="pa-error" *ngIf="resumeTouched && !resumeFile">A resume is required.</p>
      </section>

      <!-- ── Submit ───────────────────────────────────────────────── -->
      <div class="pa-submit-row">
        <button type="submit" class="pa-submit-btn" [disabled]="submitting">
          <span *ngIf="!submitting">Submit application</span>
          <span *ngIf="submitting">Submitting…</span>
        </button>
      </div>
    </form>
  </div>
</div>